import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, Home, Settings, Contact, LogOut, LogIn, User, Package } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import PatchNotesModal from './PatchNotesModal';
import LoginModal from './auth/LoginModal';
import LogoutModal from './LogoutModal';
import AccountManagementModal from './AccountManagementModal';
import RequireAuthModal from './auth/RequireAuthModal';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [showPatchNotes, setShowPatchNotes] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showAccountManagement, setShowAccountManagement] = useState(false);
  const [showRequireAuth, setShowRequireAuth] = useState(false);

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleContact = () => {
    window.open('mailto:nekalabs@gmail.com?subject=Contacto desde Zeta AI', '_blank');
    onClose();
  };

  const handleAccountManagementClick = () => {
    if (user) {
      // Usuario autenticado, mostrar modal de gestión de cuenta
      setShowAccountManagement(true);
      onClose();
    } else {
      // Usuario no autenticado, mostrar modal de requerimiento de autenticación
      setShowRequireAuth(true);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />
          
          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={`fixed top-0 right-0 h-full w-80 z-50 ${
              isDark ? 'bg-slate-900' : 'bg-white'
            } shadow-2xl`}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {t('settings')}
                </h2>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-xl transition-colors duration-200 ${
                    isDark 
                      ? 'hover:bg-slate-800 text-gray-300 hover:text-white' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 p-6 space-y-6">
                {/* Theme Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">
                    {t('lightDarkMode')}
                  </span>
                  <button
                    onClick={toggleTheme}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                      isDark ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  >
                    <motion.span
                      animate={{ x: isDark ? 20 : 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ${
                        isDark ? 'ml-1' : 'ml-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Navigation Items */}
                <div className="space-y-2">
                  <button
                    onClick={() => handleNavigation('/')}
                    className={`w-full flex items-center space-x-3 p-3 rounded-xl transition-colors duration-200 ${
                      isDark 
                        ? 'hover:bg-slate-800 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Home size={20} />
                    <span>{t('home')}</span>
                  </button>

                  <button
                    onClick={() => handleNavigation('/catalog')}
                    className={`w-full flex items-center space-x-3 p-3 rounded-xl transition-colors duration-200 ${
                      isDark 
                        ? 'hover:bg-slate-800 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Package size={20} />
                    <span>Gestionar mi catálogo</span>
                  </button>

                  <button
                    onClick={() => handleNavigation('/settings')}
                    className={`w-full flex items-center space-x-3 p-3 rounded-xl transition-colors duration-200 ${
                      isDark 
                        ? 'hover:bg-slate-800 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Settings size={20} />
                    <span>{t('settings')}</span>
                  </button>

                  <button
                    onClick={handleAccountManagementClick}
                    className={`w-full flex items-center space-x-3 p-3 rounded-xl transition-colors duration-200 ${
                      isDark 
                        ? 'hover:bg-slate-800 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <User size={20} />
                    <span>Gestionar Cuenta</span>
                  </button>

                  <button
                    onClick={handleContact}
                    className={`w-full flex items-center space-x-3 p-3 rounded-xl transition-colors duration-200 ${
                      isDark 
                        ? 'hover:bg-slate-800 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Contact size={20} />
                    <span>{t('contact')}</span>
                  </button>
                </div>

                {/* Login/Logout Button */}
                <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
                  {user ? (
                    <button
                      onClick={() => setShowLogoutModal(true)}
                      className="w-full flex items-center space-x-3 p-3 rounded-xl transition-colors duration-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <LogOut size={20} />
                      <span>{t('logout')}</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowLoginModal(true)}
                      className="w-full flex items-center space-x-3 p-3 rounded-xl transition-colors duration-200 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                    >
                      <LogIn size={20} />
                      <span>{t('loginTitle')}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 dark:border-slate-700">
                <button
                  onClick={() => setShowPatchNotes(true)}
                  className="w-full text-sm text-gray-500 dark:text-gray-400 font-mono text-center hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200"
                >
                  {t('version')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
      
      {/* Patch Notes Modal */}
      <PatchNotesModal 
        isOpen={showPatchNotes} 
        onClose={() => setShowPatchNotes(false)} 
      />
      
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
      
      {/* Logout Modal */}
      <LogoutModal 
        isOpen={showLogoutModal} 
        onClose={() => setShowLogoutModal(false)} 
      />
      
      {/* Account Management Modal */}
      <AccountManagementModal 
        isOpen={showAccountManagement} 
        onClose={() => setShowAccountManagement(false)} 
      />
      
      {/* Require Auth Modal */}
      <RequireAuthModal 
        isOpen={showRequireAuth} 
        onClose={() => setShowRequireAuth(false)}
        onLoginClick={() => {
          setShowRequireAuth(false);
          setShowLoginModal(true);
        }}
      />
    </AnimatePresence>
  );
};

export default Sidebar;
